/**
 * Breadcrumb ring buffer — Sentry-inspired action trail.
 *
 * Records the last N user/system actions so every exception can carry
 * context about what happened leading up to the crash.
 *
 * Best-in-class pattern used by Sentry, Datadog RUM, and LogRocket:
 * each error gets the "breadcrumb trail" that led to it.
 *
 * Usage:
 *   addBreadcrumb({ category: "navigation", message: "/inbox" })
 *   const trail = getBreadcrumbs() // last 50 in order
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type BreadcrumbCategory =
  | "navigation"
  | "click"
  | "api"
  | "dexie"
  | "state"
  | "lifecycle"
  | "console"
  | "error"
  | "performance"

export interface Breadcrumb {
  /** When the breadcrumb was recorded (ISO string) */
  timestamp: string
  /** High-level category for filtering */
  category: BreadcrumbCategory
  /** Human-readable description of what happened */
  message: string
  /** Optional structured data */
  data?: Record<string, string | number | boolean>
  /** Severity: info | warning | error */
  level?: "info" | "warning" | "error"
}

// ---------------------------------------------------------------------------
// Ring buffer implementation
// ---------------------------------------------------------------------------

const MAX_BREADCRUMBS = 50

/** Internal circular buffer. Items at the end are newest. */
let buffer: Breadcrumb[] = []

/**
 * Add a breadcrumb to the ring buffer.
 * If the buffer is full, the oldest entry is discarded.
 */
export function addBreadcrumb(
  crumb: Omit<Breadcrumb, "timestamp"> & { timestamp?: string },
): void {
  const entry: Breadcrumb = {
    timestamp: crumb.timestamp ?? new Date().toISOString(),
    category: crumb.category,
    message: crumb.message.slice(0, 500), // Cap message length
    level: crumb.level ?? "info",
  }

  if (crumb.data) {
    entry.data = crumb.data
  }

  buffer.push(entry)
  if (buffer.length > MAX_BREADCRUMBS) {
    buffer = buffer.slice(-MAX_BREADCRUMBS)
  }
}

/**
 * Get all breadcrumbs in chronological order (oldest first).
 * Returns a shallow copy — safe to mutate.
 */
export function getBreadcrumbs(): Breadcrumb[] {
  return [...buffer]
}

/**
 * Get breadcrumbs formatted as a compact string for error properties.
 * Each line: `[timestamp] (category) message`
 * Truncated to maxLength for App Insights property limits (8192 chars).
 */
export function getBreadcrumbsAsString(maxLength = 4000): string {
  const lines = buffer.map(
    (b) =>
      `[${b.timestamp.slice(11, 23)}] (${b.category}) ${b.message}`,
  )
  let result = lines.join("\n")
  if (result.length > maxLength) {
    result = result.slice(-maxLength)
    // Trim to the next complete line
    const firstNewline = result.indexOf("\n")
    if (firstNewline > 0) {
      result = "…\n" + result.slice(firstNewline + 1)
    }
  }
  return result
}

/**
 * Clear the ring buffer.
 */
export function clearBreadcrumbs(): void {
  buffer = []
}

// ---------------------------------------------------------------------------
// Auto-breadcrumbs — install once at app startup
// ---------------------------------------------------------------------------

let autoInstalled = false

/**
 * Install automatic breadcrumb collectors.
 * Captures: clicks, navigation (popstate), visibility changes.
 * Call once at app startup. Idempotent.
 */
export function installAutoBreadcrumbs(): void {
  if (autoInstalled || typeof window === "undefined") return
  autoInstalled = true

  // ── Click tracking ──
  document.addEventListener(
    "click",
    (e) => {
      const target = e.target as HTMLElement | null
      if (!target) return

      const tag = target.tagName?.toLowerCase()
      const text =
        target.textContent?.trim().slice(0, 60) ||
        target.getAttribute("aria-label") ||
        ""
      const role = target.getAttribute("role") || ""

      addBreadcrumb({
        category: "click",
        message: `${tag}${role ? `[role=${role}]` : ""} "${text}"`,
        data: {
          tag: tag ?? "",
          ...(target.id ? { id: target.id } : {}),
          ...(target.className && typeof target.className === "string"
            ? { class: target.className.slice(0, 100) }
            : {}),
        },
      })
    },
    { capture: true, passive: true },
  )

  // ── Navigation (popstate) ──
  window.addEventListener("popstate", () => {
    addBreadcrumb({
      category: "navigation",
      message: window.location.pathname + window.location.search,
    })
  })

  // ── Visibility changes ──
  document.addEventListener("visibilitychange", () => {
    addBreadcrumb({
      category: "lifecycle",
      message: `visibility → ${document.visibilityState}`,
    })
  })

  // ── Long Tasks via PerformanceObserver ──
  if (typeof PerformanceObserver !== "undefined") {
    try {
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (entry.duration > 100) {
            addBreadcrumb({
              category: "performance",
              message: `Long task: ${Math.round(entry.duration)}ms`,
              level: entry.duration > 300 ? "warning" : "info",
              data: {
                durationMs: Math.round(entry.duration),
              },
            })
          }
        }
      })
      observer.observe({ type: "longtask", buffered: true })
    } catch {
      // PerformanceObserver longtask not supported — ignore
    }
  }
}
